import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import usePageTitle from "../hooks/usePageTitle";
import authService from "../services/discordAuth";
import "./BlogPostPage.css";

function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isLogged, setIsLogged] = useState(false);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });
  const COMMENTS_PER_PAGE = 5;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
  const token = authService.getAccessToken();

  usePageTitle(post ? post.title : "Carregando...", post ? post.description : "");

  useEffect(() => {
    setIsLogged(authService.isAuthenticated());
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.success) {
          setPost(data.data);
          fetchComments(data.data.id, 1);
          // View record
          fetch(`${BACKEND_URL}/api/blog/posts/${data.data.id}/views`, {
            method: "POST",
          });
        }
      } catch (error) {
        console.error("Erro:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, BACKEND_URL, token]);

  const fetchComments = async (postId, page = 1) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog/posts/${postId}/comments?page=${page}&limit=${COMMENTS_PER_PAGE}`);
      const data = await res.json();
      if (data.success) {
        setComments(data.data);
        setPagination(data.pagination);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Erro comentários:", error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchComments(post.id, newPage);
      document.getElementById("comments-start").scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleReaction = async (type) => {
    if (!isLogged) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog/posts/${post.id}/reactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reaction_type: type }),
      });
      const data = await res.json();
      if (data.success) {
        const updatedPost = { ...post };
        if (post.user_reaction === type) {
          updatedPost.user_reaction = null;
          updatedPost[type === "like" ? "likes_count" : "dislikes_count"]--;
        } else {
          if (post.user_reaction) {
            updatedPost[post.user_reaction === "like" ? "likes_count" : "dislikes_count"]--;
          }
          updatedPost.user_reaction = type;
          updatedPost[type === "like" ? "likes_count" : "dislikes_count"]++;
        }
        setPost(updatedPost);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting || !isLogged) return;
    try {
      setSubmitting(true);
      const res = await fetch(`${BACKEND_URL}/api/blog/posts/${post.id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      });
      const data = await res.json();
      if (data.success) {
        setNewComment("");
        fetchComments(post.id, 1);
        setPost({ ...post, comments_count: post.comments_count + 1 });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">Carregando...</div>
      </div>
    );
  if (!post)
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">Não encontrado.</div>
      </div>
    );

  return (
    <div className="blog-post-page">
      <nav className="blog-post-breadcrumb">
        <Link to="/blog">Blog</Link> <span>/</span> <span>{post.category_name}</span> <span>/</span> <span>{post.title}</span>
      </nav>

      <header className="blog-post-header">
        <div className="blog-post-header-content">
          <span className="blog-post-category">{post.category_name}</span>
          <h1 className="blog-post-title">{post.title}</h1>
          <p className="blog-post-description">{post.description}</p>
          <div className="blog-post-meta">
            <span>Por {post.author_username}</span>
            <span>•</span>
            <span>{new Date(post.published_at).toLocaleString("pt-BR")}</span>
            <span>•</span>
            <span>{post.read_time} de leitura</span>
          </div>
        </div>

        {post.image_url && (
          <div className="blog-post-header-image">
            <img src={post.image_url} alt={post.title} />
          </div>
        )}
      </header>

      <div className="blog-post-container">
        <main className="blog-post-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </main>

        <section className="post-interactions compact">
          <div className="reactions-bar">
            <span>O que achou deste artigo?</span>
            <div className="reaction-buttons">
              <button className={`reaction-btn like ${post.user_reaction === "like" ? "active" : ""}`} onClick={() => handleReaction("like")} disabled={!isLogged}>
                👍 {post.likes_count}
              </button>
              <button className={`reaction-btn dislike ${post.user_reaction === "dislike" ? "active" : ""}`} onClick={() => handleReaction("dislike")} disabled={!isLogged}>
                👎 {post.dislikes_count}
              </button>
            </div>
            {!isLogged && <span className="auth-hint">Entre para reagir</span>}
          </div>

          <div className="comments-section" id="comments-start">
            <h2>Comentários ({pagination.total})</h2>

            {isLogged && (
              <div className="comment-form-compact">
                <form onSubmit={handleCommentSubmit}>
                  <textarea placeholder="Escreva um comentário..." value={newComment} onChange={(e) => setNewComment(e.target.value)} disabled={submitting} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "10px",
                    }}
                  >
                    <button type="submit" className="comment-submit-btn" disabled={submitting || !newComment.trim()}>
                      {submitting ? "..." : "Comentar"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item-compact">
                  <img
                    src={`https://cdn.discordapp.com/avatars/${comment.user_id}/${comment.avatar}${comment.avatar.startsWith("a_") ? ".gif" : ".png"}?size=128`}
                    className="comment-avatar-small"
                    alt=""
                  />
                  <div className="comment-body">
                    <div className="comment-meta">
                      <span className="comment-author">{comment.username}</span>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </span>
                    </div>
                    <p className="comment-text">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination-compact">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="page-nav">
                  Anterior
                </button>
                {[...Array(pagination.totalPages)].map((_, i) => (
                  <button key={i + 1} onClick={() => handlePageChange(i + 1)} className={`page-num ${currentPage === i + 1 ? "active" : ""}`}>
                    {i + 1}
                  </button>
                ))}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="page-nav">
                  Próxima
                </button>
              </div>
            )}
          </div>
        </section>

        <footer
          style={{
            textAlign: "center",
            borderTop: "1px solid rgba(255,255,255,0.05)",
            paddingTop: "40px",
          }}
        >
          <Link
            to="/blog"
            style={{
              color: "#667eea",
              textDecoration: "none",
              fontWeight: "bold",
            }}
          >
            ← Voltar para o Blog
          </Link>
        </footer>
      </div>
    </div>
  );
}

export default BlogPostPage;
